// PER-PLAN feature modules (L3) — one level below account_features, one above
// per-member permissions.
//
// Shape: { byProject: { [projectId]: PlanModuleState }, ... }
//
// WHICH FIELD GATES WHAT. The server sends two different answers per module
// and they are not interchangeable:
//
//   plan_enabled  L3 ALONE — the switch an administrator set for this plan.
//                 This is what navigation and screen guards read.
//   enabled       All four layers composed (platform flag AND account
//                 entitlement AND plan switch AND this member's permission).
//                 This is for the catalog screen's explanatory copy only.
//
// Gating navigation on `enabled` would be a REGRESSION, not a stricter gate.
// A screen whose platform flag is off is already handled by withFeatureGate,
// and a screen whose account lacks the entitlement already renders its own
// opt-in wall. Subtracting those from navigation as well would hide surfaces
// that are visible today, which is the opposite of additive. The backend makes
// the same distinction: hand-placed gates call isModuleOnForPlan (L3 only) and
// only the catalog calls isModuleActive (all four).
//
// DEFAULTS. Until the fetch for a given plan lands, every module reads ON.
// That mirrors useFeatureFlag's defaultValue=true and the backend's
// resolveStored(), which treats a NULL modules column as everything enabled.
// A plan we have not heard about yet must look exactly like today's app.

import {
  SET_PLAN_MODULES,
  SET_PLAN_MODULES_LOADING,
  SET_PLAN_MODULE_PENDING,
  CLEAR_ALL,
} from '../constants';

export interface PlanModule {
  key: string;
  label: string;
  description: string;
  category: string;
  requires_ack: boolean;
  /** All four layers composed. Catalog copy only — never gate navigation on this. */
  enabled: boolean;
  /** L3 alone: the administrator's switch. This is the gate. */
  plan_enabled: boolean;
  state: string;
  reasons: string[];
}

export interface CoreCapability {
  key: string;
  label: string;
  description: string;
}

export interface PlanModuleState {
  modules: PlanModule[];
  categories: string[];
  core: CoreCapability[];
  canManage: boolean;
  manageVia: string | null;
  isMember: boolean;
  memberStateKnown: boolean;
  /** We have a real catalog. Gates fail-open: false means read everything ON. */
  fetched: boolean;
  /**
   * We have already TRIED, successfully or not. Gates the retry, and is a
   * separate flag on purpose — a failed read must not set `fetched` (that
   * would turn fail-open into "we know, and the answer is off"), but it must
   * stop useEnsurePlanModules re-firing. Collapsing the two into one flag is
   * what caused an unbounded request loop on every failed read.
   */
  attempted: boolean;
  fetching: boolean;
  /** Module keys with a toggle in flight, so the row can disable itself. */
  pending: string[];
}

export interface PlanModulesState {
  byProject: Record<string, PlanModuleState>;
}

const initialState: PlanModulesState = { byProject: {} };

export const emptyPlanModuleState: PlanModuleState = {
  modules: [],
  categories: [],
  core: [],
  canManage: false,
  manageVia: null,
  isMember: false,
  memberStateKnown: false,
  fetched: false,
  attempted: false,
  fetching: false,
  pending: [],
};

const forProject = (state: PlanModulesState, projectId: string): PlanModuleState =>
  state.byProject[projectId] || emptyPlanModuleState;

const planModulesReducer = (
  state: PlanModulesState = initialState,
  { type, payload }: { type: string; payload?: any },
): PlanModulesState => {
  switch (type) {
    case SET_PLAN_MODULES_LOADING: {
      const projectId = payload?.projectId;
      if (!projectId) return state;
      return {
        ...state,
        byProject: {
          ...state.byProject,
          [projectId]: {
          ...forProject(state, projectId),
          fetching: Boolean(payload.loading),
          // loading:false is how a FAILED read concludes — the success path
          // dispatches SET_PLAN_MODULES instead. Either way the attempt is
          // over, so stop the effect asking again.
          attempted: payload.loading ? forProject(state, projectId).attempted : true,
        },
        },
      };
    }

    case SET_PLAN_MODULES: {
      const projectId = payload?.projectId;
      if (!projectId) return state;
      const prev = forProject(state, projectId);
      return {
        ...state,
        byProject: {
          ...state.byProject,
          [projectId]: {
            ...prev,
            modules: Array.isArray(payload.modules) ? payload.modules : [],
            categories: Array.isArray(payload.categories) ? payload.categories : [],
            core: Array.isArray(payload.core) ? payload.core : [],
            canManage: Boolean(payload.canManage),
            manageVia: payload.manageVia ?? null,
            isMember: Boolean(payload.isMember),
            memberStateKnown: Boolean(payload.memberStateKnown),
            fetched: true,
            attempted: true,
            fetching: false,
          },
        },
      };
    }

    case SET_PLAN_MODULE_PENDING: {
      const projectId = payload?.projectId;
      const key = payload?.key;
      if (!projectId || !key) return state;
      const prev = forProject(state, projectId);
      const pending = payload.pending
        ? [...new Set([...prev.pending, key])]
        : prev.pending.filter((k) => k !== key);
      return {
        ...state,
        byProject: { ...state.byProject, [projectId]: { ...prev, pending } },
      };
    }

    case CLEAR_ALL:
      // Unlike the global feature-flag slice, this IS per-user, per-plan data.
      // Holding it across a logout would show the next account this account's
      // module layout for the moment before the first fetch resolved.
      return initialState;

    default:
      return state;
  }
};

export default planModulesReducer;
